"use client";

import React, { useEffect, useMemo, useState, Suspense } from "react";
import Image from "next/image";
import { useRouter, useSearchParams } from "next/navigation";
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Snackbar,
} from "@mui/material";
import LoginIcon from "@mui/icons-material/Login";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ShieldMoonIcon from "@mui/icons-material/ShieldMoon";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useAuth } from "hooks/useAuth";

const DEFAULT_USERNAME = "";
const DEFAULT_PASSWORD = "";

type ResetStep = "request" | "verify";

// Component to handle search params with Suspense boundary
function SearchParamsHandler({
  setFeedback,
}: {
  setFeedback: React.Dispatch<
    React.SetStateAction<{ type: "success" | "error"; message: string } | null>
  >;
}) {
  const searchParams = useSearchParams();

  useEffect(() => {
    const reason = searchParams?.get("reason");
    if (reason === "session_revoked") {
      setFeedback({
        type: "error",
        message: "Your session has been revoked. Please sign in again.",
      });
    }
  }, [searchParams, setFeedback]);

  return null;
}

function LoginForm() {
  const router = useRouter();
  const { login, user, loading } = useAuth();

  const [activeTab, setActiveTab] = useState<"login" | "reset">("login");
  const [identifier, setIdentifier] = useState(DEFAULT_USERNAME);
  const [password, setPassword] = useState(DEFAULT_PASSWORD);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [resetStep, setResetStep] = useState<ResetStep>("request");
  const [resetEmail, setResetEmail] = useState("");
  const [resetOtp, setResetOtp] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  // setFeedback from useState is stable; pass it directly to the SearchParamsHandler

  useEffect(() => {
    if (user) {
      router.push("/dashboard");
    }
  }, [router, user]);

  // Open snackbar whenever feedback changes so we don't render an inline Alert
  useEffect(() => {
    if (feedback) setSnackbarOpen(true);
  }, [feedback]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFeedback(null);
    setSubmitting(true);

    try {
      const normalizedIdentifier = identifier.trim();
      await login({ identifier: normalizedIdentifier, password });
      setFeedback({
        type: "success",
        message: "Signed in successfully. Redirecting…",
      });
      // router.push('/users') this action is not needed
    } catch (err: any) {
      const message = err?.message || "Unable to sign in";
      // If server responded with an entity-level denial, the global auth snackbar
      // will already display the friendly message. Suppress the local form snackbar
      // to avoid duplicate toasts.
      if (/access\s*denied/i.test(String(message))) {
        setFeedback(null);
        setSnackbarOpen(false);
      } else {
        setFeedback({ type: "error", message });
        setSnackbarOpen(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const requestOtp = async () => {
    setFeedback(null);
    setResetLoading(true);
    try {
      const res = await fetch("/api/auth/forgot-password-otp", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: resetEmail }),
      });
      if (!res.ok)
        throw new Error((await res.json()).error || "Unable to send OTP");
      setFeedback({
        type: "success",
        message: "If the account exists, an OTP has been delivered.",
      });
      setResetStep("verify");
    } catch (err: any) {
      setFeedback({
        type: "error",
        message: err.message || "Unable to send OTP",
      });
    } finally {
      setResetLoading(false);
    }
  };

  const verifyAndReset = async () => {
    setFeedback(null);
    setResetLoading(true);
    try {
      const verifyRes = await fetch("/api/auth/verify-password-reset-otp", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token: resetOtp }),
      });
      if (!verifyRes.ok)
        throw new Error((await verifyRes.json()).error || "Invalid token");

      const resetRes = await fetch("/api/auth/reset-password-otp", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token: resetOtp, password: resetPassword }),
      });
      if (!resetRes.ok)
        throw new Error((await resetRes.json()).error || "Reset failed");
      setFeedback({
        type: "success",
        message: "Password reset. You can sign in with your new credentials.",
      });
      setResetStep("request");
      setResetEmail("");
      setResetOtp("");
      setResetPassword("");
      setActiveTab("login");
    } catch (err: any) {
      setFeedback({
        type: "error",
        message: err.message || "Unable to reset password",
      });
    } finally {
      setResetLoading(false);
    }
  };

  const headline = useMemo(
    () => (activeTab === "login" ? "Welcome back" : "Secure access reset"),
    [activeTab],
  );

  return (
    <Container maxWidth="lg">
      <Suspense fallback={null}>
        <SearchParamsHandler setFeedback={setFeedback} />
      </Suspense>
      <Paper
        elevation={0}
        sx={{
          overflow: "hidden",
          borderRadius: { xs: 4, md: 6 },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          background: "linear-gradient(135deg, #ffffff 0%, #f6f9ff 100%)",
        }}
      >
        <Box
          sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}
        >
          <Box
            sx={{
              width: { xs: "100%", md: "50%" },
              background: "linear-gradient(160deg, #152238 0%, #203352 100%)",
              color: "#fff",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              p: { xs: 4, md: 6 },
            }}
          >
            <Stack spacing={4}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    borderRadius: "20%",
                    overflow: "hidden",
                    width: 52,
                    height: 52,
                  }}
                >
                  <Image
                    src="/origin-north-horizontal-2.png"
                    width={130}
                    height={52}
                    alt="Origin-North logo"
                  />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    Origin-North IAM
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    Enterprise-ready identity, access, and audit controls
                    designed for automation.
                  </Typography>
                </Box>
              </Box>
              <Stack spacing={2}>
                <Typography variant="h4" component="h1" fontWeight={700}>
                  {headline}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.8 }}>
                  Manage users, roles, permissions, and entities from a single
                  secure workspace. All changes are tracked for SOC2, GDPR, and
                  PIPEDA compliance.
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <ShieldMoonIcon fontSize="large" />
                <Typography
                  variant="caption"
                  sx={{ letterSpacing: "0.08em", textTransform: "uppercase" }}
                >
                  Zero trust ready · MFA enforced · Continuous audit
                </Typography>
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ width: { xs: "100%", md: "50%" }, p: { xs: 4, md: 6 } }}>
            <Stack spacing={3}>
              <Tabs
                value={activeTab}
                onChange={(_event, value) =>
                  setActiveTab(value as "login" | "reset")
                }
                aria-label="Authentication tabs"
                variant="fullWidth"
              >
                <Tab label="Sign in" value="login" />
                <Tab label="Reset password" value="reset" />
              </Tabs>

              {/* feedback is shown via Snackbar; inline Alert removed */}

              {activeTab === "login" ? (
                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <Stack spacing={2.5}>
                    <TextField
                      label="Email or username"
                      value={identifier}
                      onChange={(event) => setIdentifier(event.target.value)}
                      autoComplete="username"
                      required
                      fullWidth
                    />
                    <TextField
                      label="Password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      autoComplete="current-password"
                      required
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={
                                showPassword ? "Hide password" : "Show password"
                              }
                              onClick={() => setShowPassword((s) => !s)}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={2}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Need to onboard a user? Invite them once you sign in.
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => setActiveTab("reset")}
                        sx={{ fontWeight: 600 }}
                      >
                        Forgot password?
                      </Button>
                    </Stack>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      startIcon={<LoginIcon />}
                      disabled={submitting || loading}
                    >
                      {submitting || loading ? "Signing in…" : "Sign in"}
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <Stack spacing={3}>
                  {resetStep === "request" ? (
                    <Stack spacing={2.5}>
                      <TextField
                        label="Work email"
                        value={resetEmail}
                        onChange={(event) => setResetEmail(event.target.value)}
                        type="email"
                        fullWidth
                      />
                      <Button
                        variant="contained"
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        onClick={requestOtp}
                        disabled={resetLoading}
                      >
                        {resetLoading ? "Sending…" : "Send One-Time Passcode"}
                      </Button>
                    </Stack>
                  ) : (
                    <Stack spacing={2.5}>
                      <TextField
                        label="One-Time Passcode"
                        value={resetOtp}
                        onChange={(event) => setResetOtp(event.target.value)}
                        fullWidth
                      />
                      <TextField
                        label="New password"
                        type="password"
                        value={resetPassword}
                        onChange={(event) =>
                          setResetPassword(event.target.value)
                        }
                        fullWidth
                      />
                      <Button
                        variant="contained"
                        size="large"
                        onClick={verifyAndReset}
                        disabled={resetLoading}
                      >
                        {resetLoading ? "Updating…" : "Verify & reset"}
                      </Button>
                    </Stack>
                  )}
                </Stack>
              )}

              <Typography
                variant="caption"
                color="text.secondary"
                textAlign="center"
              >
                By accessing the console you acknowledge ongoing monitoring and
                consent to the Origin-North acceptable use policy.
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Paper>
      {feedback && (
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
        >
          <Alert
            severity={feedback.type}
            onClose={() => setSnackbarOpen(false)}
            sx={{ width: "100%" }}
          >
            {feedback.message}
          </Alert>
        </Snackbar>
      )}
    </Container>
  );
}

export default function LoginPage() {
  return <LoginForm />;
}
